import { CINEMETA_URL } from '../constants'
import type {
  EngineFile,
  EngineStats,
  MediaType,
  Meta,
  MetaPreview,
  PlayableStream,
  Settings,
  StremioStream,
  Subtitle,
  Video,
} from '../types'

const VIDEO_EXT = /\.(mkv|mp4|avi|mov|m4v|webm|ts|m2ts)$/i
const MAX_JSON_BYTES = 4 * 1024 * 1024

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function text(value: unknown, maximum: number) {
  return typeof value === 'string' ? value.slice(0, maximum) : undefined
}

function requiredText(value: unknown, maximum: number) {
  const result = text(value, maximum)
  return result && result.trim() ? result : undefined
}

function httpUrl(value: unknown, maximum = 4_096) {
  if (typeof value !== 'string' || !value || value.length > maximum) return undefined
  try {
    const url = new URL(value)
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname ? value : undefined
  } catch { return undefined }
}

function stringList(value: unknown, maximumItems: number, maximumLength: number) {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && Boolean(entry))
      .slice(0, maximumItems).map((entry) => entry.slice(0, maximumLength))
    : undefined
}

function finite(value: unknown, minimum: number, maximum: number) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(minimum, Math.min(maximum, value)) : undefined
}

function cleanVideo(value: unknown): Video | null {
  if (!record(value)) return null
  const id = requiredText(value.id, 240)
  if (!id) return null
  const chapters = Array.isArray(value.chapters) ? value.chapters.flatMap((chapter) => {
    if (!record(chapter)) return []
    const title = requiredText(chapter.title, 160)
    const start = finite(chapter.start, 0, 86_400)
    return title && start !== undefined ? [{ title, start }] : []
  }).slice(0, 100) : undefined
  return {
    id,
    title: text(value.title, 200),
    name: text(value.name, 200),
    season: Number.isInteger(value.season) ? finite(value.season, 0, 100_000) : undefined,
    episode: Number.isInteger(value.episode) ? finite(value.episode, 0, 100_000) : undefined,
    released: text(value.released, 80),
    thumbnail: httpUrl(value.thumbnail, 2_000),
    overview: text(value.overview, 1_200),
    chapters,
    introStart: finite(value.introStart, 0, 86_400),
    introEnd: finite(value.introEnd, 0, 86_400),
    creditsStart: finite(value.creditsStart, 0, 86_400),
  }
}

function cleanMetaPreview(value: unknown, fallbackType?: MediaType, origin?: string): MetaPreview | null {
  if (!record(value)) return null
  const id = requiredText(value.id, 240)
  const name = requiredText(value.name, 200)
  const type = value.type === 'movie' || value.type === 'series' ? value.type : fallbackType
  if (!id || !name || !type) return null
  const year = typeof value.year === 'number' && Number.isFinite(value.year)
    ? Math.max(0, Math.min(9999, value.year)) : text(value.year, 20)
  return {
    id,
    type,
    name,
    imdb_id: text(value.imdb_id, 240),
    poster: httpUrl(value.poster, 2_000),
    background: httpUrl(value.background, 2_000),
    logo: httpUrl(value.logo, 2_000),
    description: text(value.description, 2_000),
    releaseInfo: text(value.releaseInfo, 80),
    year,
    imdbRating: text(value.imdbRating, 20),
    runtime: text(value.runtime, 40),
    genre: stringList(value.genre, 12, 40),
    genres: stringList(value.genres, 12, 40),
    released: text(value.released, 80),
    addonUrl: origin || httpUrl(value.addonUrl, 500),
  }
}

function cleanMeta(value: unknown, fallbackType: MediaType, origin?: string): Meta | null {
  const preview = cleanMetaPreview(value, fallbackType, origin)
  if (!preview || !record(value)) return null
  const director = Array.isArray(value.director)
    ? stringList(value.director, 12, 100)
    : text(value.director, 100)
  return {
    ...preview,
    cast: stringList(value.cast, 24, 100),
    director,
    videos: Array.isArray(value.videos)
      ? value.videos.map(cleanVideo).filter((entry): entry is Video => Boolean(entry)).slice(0, 500)
      : undefined,
    trailers: Array.isArray(value.trailers) ? value.trailers.flatMap((trailer) => {
      if (!record(trailer)) return []
      const source = requiredText(trailer.source, 1_000)
      return source ? [{ source, type: text(trailer.type, 40) }] : []
    }).slice(0, 20) : undefined,
    trailerStreams: Array.isArray(value.trailerStreams) ? value.trailerStreams.flatMap((trailer) => {
      if (!record(trailer)) return []
      const ytId = requiredText(trailer.ytId, 120)
      return ytId ? [{ ytId, title: text(trailer.title, 200) }] : []
    }).slice(0, 20) : undefined,
  }
}

function cleanHeaderMap(value: unknown) {
  if (!record(value)) return undefined
  const entries = Object.entries(value).flatMap(([key, header]) => (
    /^[!#$%&'*+.^_`|~0-9A-Za-z-]{1,80}$/.test(key) && typeof header === 'string'
      ? [[key, header.slice(0, 2_000)] as const] : []
  )).slice(0, 24)
  return entries.length ? Object.fromEntries(entries) : undefined
}

function cleanStream(value: unknown): StremioStream | null {
  if (!record(value)) return null
  const url = httpUrl(value.url)
  const externalUrl = httpUrl(value.externalUrl)
  const ytId = requiredText(value.ytId, 120)
  const infoHash = typeof value.infoHash === 'string' && /^[a-f0-9]{40}$/i.test(value.infoHash)
    ? value.infoHash.toLowerCase() : undefined
  if (!url && !externalUrl && !ytId && !infoHash) return null
  const hints = record(value.behaviorHints) ? {
    bingeGroup: text(value.behaviorHints.bingeGroup, 200),
    filename: text(value.behaviorHints.filename, 500),
    videoSize: finite(value.behaviorHints.videoSize, 0, 10_000_000_000_000),
    notWebReady: typeof value.behaviorHints.notWebReady === 'boolean' ? value.behaviorHints.notWebReady : undefined,
    proxyHeaders: record(value.behaviorHints.proxyHeaders) ? {
      request: cleanHeaderMap(value.behaviorHints.proxyHeaders.request),
      response: cleanHeaderMap(value.behaviorHints.proxyHeaders.response),
    } : undefined,
  } : undefined
  return {
    name: text(value.name, 300),
    title: text(value.title, 1_200),
    description: text(value.description, 1_200),
    url,
    externalUrl,
    ytId,
    infoHash,
    fileIdx: Number.isInteger(value.fileIdx) ? finite(value.fileIdx, 0, 100_000) : undefined,
    sources: stringList(value.sources, 64, 500),
    fileMustInclude: stringList(value.fileMustInclude, 20, 200),
    addonUrl: httpUrl(value.addonUrl, 500),
    behaviorHints: hints,
  }
}

export function normalizeUrl(value: string) {
  return value.trim().replace(/\/manifest\.json\/?$/i, '').replace(/\/+$/, '')
}

export function isHttpUrl(value: string) {
  try {
    const url = new URL(value.trim())
    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname)
  } catch { return false }
}

export async function getJson<T>(url: string, init?: RequestInit, timeout = 20_000): Promise<T> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined
  const timer = setTimeout(() => controller?.abort(), timeout)
  try {
    const response = await fetch(url, { ...init, signal: controller?.signal })
    if (!response.ok) throw new Error(`Request failed (${response.status})`)
    const declared = Number(response.headers?.get?.('Content-Length') || 0)
    if (declared > MAX_JSON_BYTES) throw new Error('Remote JSON response is too large.')
    // Test doubles from older suites may provide json() only. Real browser
    // Responses always take the bounded text path before JSON parsing.
    if (typeof response.text !== 'function') return (await response.json()) as T
    const raw = await response.text()
    // UTF-8 byte length is never below the UTF-16 code-unit count, so this
    // check already covers the byte budget. Encoding the string purely to
    // measure it allocated a second copy of every catalogue page.
    if (raw.length > MAX_JSON_BYTES) throw new Error('Remote JSON response is too large.')
    return JSON.parse(raw) as T
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw new Error('Request timed out')
    throw error
  } finally {
    clearTimeout(timer)
  }
}

export interface CatalogOptions {
  genre?: string
  skip?: number
  search?: string
}

function catalogExtra(options: CatalogOptions) {
  const entries: string[] = []
  if (options.genre) entries.push(`genre=${encodeURIComponent(options.genre)}`)
  if (options.skip) entries.push(`skip=${Math.max(0, Math.floor(options.skip))}`)
  if (options.search) entries.push(`search=${encodeURIComponent(options.search)}`)
  return entries.join('&')
}

export async function getCatalog(type: MediaType, catalog = 'top', options: CatalogOptions = {}) {
  const extra = catalogExtra(options)
  const data = await getJson<unknown>(
    `${CINEMETA_URL}/catalog/${type}/${catalog}${extra ? `/${extra}` : ''}.json`,
  )
  const metas = record(data) && Array.isArray(data.metas) ? data.metas : []
  return metas.map((item) => cleanMetaPreview(item, type))
    .filter((item): item is MetaPreview => Boolean(item?.poster)).slice(0, 500)
}

/** Fetch enough real catalog pages for a TV browse page without rendering an
 * unbounded poster wall. Cinemeta pages are twenty items each. */
export async function getCatalogPages(type: MediaType, catalog = 'top', options: CatalogOptions = {}, pages = 2) {
  const count = Math.max(1, Math.min(10, Math.floor(pages) || 1))
  const results = await Promise.allSettled(Array.from({ length: count }, (_, index) =>
    getCatalog(type, catalog, { ...options, skip: (options.skip || 0) + index * 20 }),
  ))
  const firstSuccess = results.find((result) => result.status === 'fulfilled')
  if (!firstSuccess) {
    const firstFailure = results.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined
    throw firstFailure?.reason instanceof Error ? firstFailure.reason : new Error('Could not load this catalog.')
  }
  const seen = new Set<string>()
  return results.flatMap((result) => result.status === 'fulfilled' ? result.value : []).filter((item) => {
    const key = `${item.type}:${item.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export interface AddonCatalogExtra {
  name: string
  isRequired?: boolean
  options?: string[]
  optionsLimit?: number
}

export interface AddonCatalogDescriptor {
  type: MediaType
  id: string
  name?: string
  extra?: AddonCatalogExtra[]
}

export interface StremioAddonManifest {
  id?: string
  name?: string
  resources?: unknown[]
  catalogs?: AddonCatalogDescriptor[]
}

export async function getAddonManifest(addonUrl: string) {
  const data = await getJson<unknown>(`${normalizeUrl(addonUrl)}/manifest.json`, undefined, 12_000)
  if (!record(data)) throw new Error('The add-on manifest is not valid.')
  const resources = Array.isArray(data.resources) ? data.resources.flatMap((resource): Array<string | { name: string }> => {
    if (typeof resource === 'string' && resource.length <= 80) return [resource]
    if (!record(resource) || typeof resource.name !== 'string' || !resource.name || resource.name.length > 80) return []
    return [{ name: resource.name }]
  }).slice(0, 32) : undefined
  const catalogs = Array.isArray(data.catalogs) ? data.catalogs.flatMap((catalog): AddonCatalogDescriptor[] => {
    if (!record(catalog) || (catalog.type !== 'movie' && catalog.type !== 'series')) return []
    const id = requiredText(catalog.id, 120)
    if (!id) return []
    const extra = Array.isArray(catalog.extra) ? catalog.extra.flatMap((entry): AddonCatalogExtra[] => {
      if (!record(entry) || !['genre', 'skip', 'search'].includes(entry.name as string)) return []
      return [{
        name: entry.name as string,
        isRequired: entry.isRequired === true,
        options: stringList(entry.options, 100, 80),
        optionsLimit: Number.isInteger(entry.optionsLimit) ? finite(entry.optionsLimit, 0, 1_000) : undefined,
      }]
    }).slice(0, 12) : undefined
    return [{ type: catalog.type, id, name: text(catalog.name, 120), extra }]
  }).slice(0, 100) : undefined
  return {
    id: text(data.id, 80),
    name: text(data.name, 80),
    resources,
    catalogs,
  }
}

export function addonCatalogSupports(catalog: AddonCatalogDescriptor, extra: keyof CatalogOptions) {
  return Boolean(catalog.extra?.some((entry) => entry.name === extra))
}

export async function getAddonCatalog(
  addonUrl: string,
  catalog: AddonCatalogDescriptor,
  options: CatalogOptions = {},
) {
  const supported = Object.fromEntries(Object.entries(options).filter(([name]) => addonCatalogSupports(catalog, name as keyof CatalogOptions))) as CatalogOptions
  const extra = catalogExtra(supported)
  const data = await getJson<unknown>(
    `${normalizeUrl(addonUrl)}/catalog/${encodeURIComponent(catalog.type)}/${encodeURIComponent(catalog.id)}${extra ? `/${extra}` : ''}.json`,
  )
  const origin = normalizeUrl(addonUrl)
  const metas = record(data) && Array.isArray(data.metas) ? data.metas : []
  return metas.map((item) => cleanMetaPreview(item, catalog.type, origin))
    .filter((item): item is MetaPreview => Boolean(item?.poster)).slice(0, 500)
}

export async function getAddonCatalogPages(
  addonUrl: string,
  catalog: AddonCatalogDescriptor,
  options: CatalogOptions = {},
  pages = 2,
) {
  const canSkip = addonCatalogSupports(catalog, 'skip')
  const count = canSkip ? Math.max(1, Math.min(10, Math.floor(pages) || 1)) : 1
  const requests = Array.from({ length: count }, (_, index) =>
    getAddonCatalog(addonUrl, catalog, { ...options, skip: (options.skip || 0) + index * 20 }))
  const results = await Promise.allSettled(requests)
  const firstSuccess = results.find((result) => result.status === 'fulfilled')
  if (!firstSuccess) {
    const failure = results.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined
    throw failure?.reason instanceof Error ? failure.reason : new Error('Could not load this add-on catalog.')
  }
  const seen = new Set<string>()
  return results.flatMap((result) => result.status === 'fulfilled' ? result.value : []).filter((item) => {
    const key = `${item.type}:${item.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export async function searchCatalog(query: string) {
  const original = query.trim()
  if (!original) return []
  const folded = original.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  const queries = Array.from(new Set([original, folded].filter(Boolean)))
  const results = await Promise.allSettled(queries.flatMap((value) => {
    const extra = `search=${encodeURIComponent(value)}`
    return [
      getJson<unknown>(`${CINEMETA_URL}/catalog/movie/top/${extra}.json`),
      getJson<unknown>(`${CINEMETA_URL}/catalog/series/top/${extra}.json`),
    ]
  }))
  if (!results.some((result) => result.status === 'fulfilled')) {
    const failure = results.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined
    throw failure?.reason instanceof Error ? failure.reason : new Error('Search providers are unavailable.')
  }
  const all = results.flatMap((result) => (
    result.status === 'fulfilled' && record(result.value) && Array.isArray(result.value.metas)
      ? result.value.metas : []
  )).map((item) => cleanMetaPreview(item)).filter((item): item is MetaPreview => Boolean(item)).slice(0, 1_000)
  const seen = new Set<string>()
  return all.filter((item) => {
    const key = `${item.type}:${item.id}`
    if (!item.id || !item.poster || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export async function getMeta(type: MediaType, id: string, addonUrl?: string) {
  const base = addonUrl ? normalizeUrl(addonUrl) : CINEMETA_URL
  const data = await getJson<unknown>(`${base}/meta/${type}/${encodeURIComponent(id)}.json`)
  const meta = record(data) ? cleanMeta(data.meta, type, addonUrl ? base : undefined) : null
  if (!meta) throw new Error('The metadata provider returned an invalid title.')
  return meta
}

export async function getStreams(
  addonUrl: string,
  type: MediaType,
  id: string,
): Promise<StremioStream[]> {
  const base = normalizeUrl(addonUrl)
  const data = await getJson<unknown>(
    `${base}/stream/${type}/${encodeURIComponent(id)}.json`,
    undefined,
    35_000,
  )
  const streams = record(data) && Array.isArray(data.streams) ? data.streams : []
  return streams.map(cleanStream).filter((stream): stream is StremioStream => Boolean(stream)).slice(0, 1_000)
}

export async function getSubtitles(
  addonUrl: string,
  type: MediaType,
  id: string,
): Promise<Subtitle[]> {
  const base = normalizeUrl(addonUrl)
  const data = await getJson<unknown>(
    `${base}/subtitles/${type}/${encodeURIComponent(id)}.json`,
    undefined,
    20_000,
  )
  const subtitles = record(data) && Array.isArray(data.subtitles) ? data.subtitles : []
  return subtitles.flatMap((item): Subtitle[] => {
    if (!record(item)) return []
    const subtitleId = requiredText(item.id, 300)
    const url = httpUrl(item.url)
    const lang = requiredText(item.lang, 12)
    return subtitleId && url && lang ? [{ id: subtitleId, url, lang }] : []
  }).slice(0, 500)
}

function matchesEpisode(path: string, video?: Video) {
  if (!video?.season || !video?.episode) return false
  const season = video.season
  const episode = video.episode
  const patterns = [
    new RegExp(`s0*${season}[^a-z0-9]?e0*${episode}(?:[^0-9]|$)`, 'i'),
    new RegExp(`(?:^|[^0-9])0*${season}x0*${episode}(?:[^0-9]|$)`, 'i'),
    new RegExp(`season[^0-9]*0*${season}.*episode[^0-9]*0*${episode}(?:[^0-9]|$)`, 'i'),
  ]
  return patterns.some((pattern) => pattern.test(path))
}

/**
 * The requested episode wins outright; size only breaks ties. Adding a fixed
 * episode bonus to the byte length used to let any file more than that bonus
 * larger — a 4K remux in the same season pack — outrank the episode actually
 * asked for.
 */
export function selectFileIndex(files: EngineFile[], video?: Video) {
  let selected = -1
  let bestMatches = false
  let bestLength = -1
  files.forEach((file, index) => {
    if (!VIDEO_EXT.test(file.path) || /(^|[._ -])sample([._ -]|$)/i.test(file.path)) return
    const matches = matchesEpisode(file.path, video)
    const length = file.length || 0
    if (selected >= 0 && (bestMatches !== matches ? !matches : length <= bestLength)) return
    bestMatches = matches
    bestLength = length
    selected = index
  })
  return selected >= 0 ? selected : 0
}

function proxyUrl(serverUrl: string, stream: StremioStream) {
  if (!stream.url || !stream.behaviorHints?.proxyHeaders) return stream.url
  const source = new URL(stream.url)
  const query = new URLSearchParams()
  query.set('d', source.origin)
  Object.entries(stream.behaviorHints.proxyHeaders.request || {}).forEach(([key, value]) =>
    query.append('h', `${key}:${value}`),
  )
  Object.entries(stream.behaviorHints.proxyHeaders.response || {}).forEach(([key, value]) =>
    query.append('r', `${key}:${value}`),
  )
  return `${serverUrl}/proxy/${query.toString()}/${source.pathname.slice(1)}${source.search}`
}

function hlsSessionId(mediaUrl: string) {
  let hash = 0x811c9dc5
  for (let index = 0; index < mediaUrl.length; index += 1) {
    hash = Math.imul(hash ^ mediaUrl.charCodeAt(index), 0x01000193)
  }
  return `vela-${(hash >>> 0).toString(36)}`
}

function serverHlsV2Url(serverUrl: string, mediaUrl: string) {
  // The legacy /master.m3u8 route in Stremio Server 4.21 emits stream-N
  // children that its quality guard rejects with 404. HLS v2 exposes the same
  // independent audio renditions as valid fMP4 playlists. Keep the absolute
  // source in a query because Apache rejects encoded slashes in path params.
  return `${serverUrl}/hlsv2/${hlsSessionId(mediaUrl)}/master.m3u8?mediaURL=${encodeURIComponent(mediaUrl)}&maxAudioChannels=2`
}

export interface ServerAudioTrack {
  stream: number
  codec: string
  language: string
  default: boolean
}

/** Probe only track metadata. No result is trusted until every field is
 * bounded; the playback URL remains on the TV and configured server. */
export async function getServerAudioTracks(serverUrl: string, mediaUrl: string) {
  const server = normalizeUrl(serverUrl)
  const data = await getJson<unknown>(
    `${server}/probe?url=${encodeURIComponent(mediaUrl)}`,
    undefined,
    25_000,
  )
  const streams = record(data) && Array.isArray(data.streams) ? data.streams : []
  return streams.flatMap((entry): ServerAudioTrack[] => {
    if (!record(entry)) return []
    if (entry.codec_type !== 'audio' || !Number.isInteger(entry.stream) || Number(entry.stream) < 0 || Number(entry.stream) > 99) return []
    return [{
      stream: Number(entry.stream),
      codec: typeof entry.codec_name === 'string' ? entry.codec_name.slice(0, 24) : 'unknown',
      language: typeof entry.lang === 'string' ? entry.lang.slice(0, 12) : '',
      default: entry.default === true,
    }]
  }).slice(0, 24)
}

export async function prepareStream(
  stream: StremioStream,
  video: Video | undefined,
  settings: Settings,
  options: { transport?: 'hls' | 'direct' } = {},
): Promise<PlayableStream> {
  const server = normalizeUrl(settings.serverUrl)

  if (stream.url) {
    const directUrl = proxyUrl(server, stream) || stream.url
    const needsServer = Boolean(stream.behaviorHints?.notWebReady && !stream.behaviorHints.proxyHeaders)
    const audioHlsUrl = serverHlsV2Url(server, directUrl)
    // Keep both transports for every HTTP source. Even a nominally web-ready
    // signed MP4 can expose a container/audio quirk or stall in LG's direct
    // pipeline; HLS v2 is the bounded recovery path rather than a dead end.
    const hlsUrl = audioHlsUrl
    // Preserve lossless/direct playback for web-ready media (especially HDR)
    // unless compatibility evidence explicitly chose HLS. notWebReady sources
    // still honor the user's HLS preference as before.
    const useHls = options.transport ? options.transport === 'hls' : needsServer && settings.preferHls
    return {
      url: useHls ? hlsUrl : directUrl,
      directUrl,
      hlsUrl,
      audioHlsUrl,
      probeUrl: directUrl,
      transport: useHls ? 'hls' : 'direct',
    }
  }

  if (!stream.infoHash || !/^[a-f0-9]{40}$/i.test(stream.infoHash)) {
    throw new Error('This source does not contain a playable URL or torrent hash.')
  }

  const infoHash = stream.infoHash.toLowerCase()
  let fileIdx = Number.isInteger(stream.fileIdx) && Number(stream.fileIdx) >= 0
    ? Number(stream.fileIdx)
    : undefined

  if (fileIdx === undefined) {
    const sources = Array.from(new Set([`dht:${infoHash}`, ...(stream.sources || [])]))
    const payload: Record<string, unknown> = {
      torrent: { infoHash },
      peerSearch: { sources, min: 40, max: 150 },
    }
    if (stream.fileMustInclude?.length) payload.fileMustInclude = stream.fileMustInclude

    const created = await getJson<unknown>(
      `${server}/${infoHash}/create`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      45_000,
    )
    const guessed = record(created) && Number.isInteger(created.guessedFileIdx)
      ? finite(created.guessedFileIdx, 0, 100_000) : undefined
    const files: EngineFile[] = record(created) && Array.isArray(created.files)
      ? created.files.flatMap((file): EngineFile[] => {
        if (!record(file)) return []
        const filePath = requiredText(file.path, 1_000)
        const length = finite(file.length, 0, 10_000_000_000_000)
        return filePath && length !== undefined ? [{ path: filePath, length }] : []
      }).slice(0, 10_000) : []
    fileIdx = guessed ?? selectFileIndex(files, video)
  }

  const directUrl = `${server}/${infoHash}/${fileIdx}`
  const hlsUrl = `${directUrl}/hls.m3u8`
  const useHls = options.transport ? options.transport === 'hls' : settings.preferHls
  return {
    url: useHls ? hlsUrl : directUrl,
    directUrl,
    hlsUrl,
    // /hls.m3u8 is the public Stremio route into its working HLS v2 master.
    audioHlsUrl: hlsUrl,
    probeUrl: directUrl,
    transport: useHls ? 'hls' : 'direct',
    statsUrl: `${directUrl}/stats.json`,
    infoHash,
    fileIdx,
  }
}

export async function getEngineStats(url: string) {
  const data = await getJson<unknown>(url, undefined, 5_000)
  if (!record(data)) return {}
  const result: EngineStats = {}
  const limits: Array<[keyof EngineStats, number]> = [
    ['peers', 1_000_000], ['downloadSpeed', 10_000_000_000], ['uploaded', 10_000_000_000_000],
    ['downloaded', 10_000_000_000_000], ['streamLen', 10_000_000_000_000], ['streamProgress', 1], ['queued', 1_000_000],
  ]
  limits.forEach(([key, maximum]) => {
    const value = finite(data[key], 0, maximum)
    if (value !== undefined) result[key] = value
  })
  return result
}

export async function testServer(serverUrl: string) {
  const server = normalizeUrl(serverUrl)
  const settings = await getJson<unknown>(`${server}/settings`, undefined, 8_000)
  const values = record(settings) && record(settings.values) ? settings.values : null
  const version = values ? requiredText(values.serverVersion, 80) : undefined
  if (!version) throw new Error('Connected, but this is not a Stremio streaming server.')
  return version
}

export async function testAddon(addonUrl: string) {
  const manifest = await getAddonManifest(addonUrl)
  if (!manifest.resources) throw new Error('The add-on manifest is not valid.')
  return manifest.name || 'Stremio add-on'
}
